import { useMemo } from 'react';
import { Box, Divider, Flex, Group, Stack, Text } from '@mantine/core';
import {
	getDisplayWorkflowStatusText,
	mapToDisplayStatus,
	DisplayWorkflowStatusColorMap,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils';
import {
	formatDateToUI,
	parseBEDateIgnoreTimezone,
	DATE_FORMATS,
} from '@dealstack/shared/lib/format/dates/date';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	DocumentWorkflowEventLogType,
	DocumentWorkflowInstanceSignatureStatus,
	DocumentWorkflowInstanceStatus,
	DocumentWorkflowTemplateType,
} from '@dealstack/shared/lib/generated';
import {
	canReplaceDocumentFile,
	isReviewDocument,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/DetailsTab.utils';
import { canCloseDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/CloseDocumentSection.utils';
import { CloseDocumentSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/CloseDocumentSection';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useIsDealstackSupport } from '@dealstack/frontend/shared/hooks/useIsDealstackSupport';
import { useDocumentWorkflowEvents } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useDocumentWorkflowEvents';
import { resolveActor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/ActivityTab.utils';
import { ReplaceFileSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/ReplaceFileSection';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	fileName?: string | null;
	createdAt: Date;
	updatedAt: Date;
	workflowStatus: DocumentWorkflowInstanceStatus;
	documentWorkflowTemplateType: DocumentWorkflowTemplateType | undefined;
	requiresPostDating: boolean;
	dateAppliedAt: string | null;
	effectiveDate: string | null;
	statusMessage: string | null;
	signerStatuses: Array<DocumentWorkflowInstanceSignatureStatus | undefined>;
}

const formatDisplayDate = (date: Date): string =>
	formatDateToUI(date, DATE_FORMATS.CALENDAR_DATE) ?? '—';

export const DetailsTab = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	fileName,
	createdAt,
	updatedAt,
	workflowStatus,
	documentWorkflowTemplateType,
	requiresPostDating,
	dateAppliedAt,
	effectiveDate,
	statusMessage,
	signerStatuses,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const isSupport = useIsDealstackSupport();

	const displayStatus = mapToDisplayStatus(workflowStatus, { requiresPostDating, dateAppliedAt });
	const statusColor = DisplayWorkflowStatusColorMap[displayStatus];
	const statusText = getDisplayWorkflowStatusText(displayStatus, tMipManage);

	const canReplaceFile = canReplaceDocumentFile({ isSupport, workflowStatus });
	const isReadOnly = isReviewDocument(documentWorkflowTemplateType);

	const { data: eventsData } = useDocumentWorkflowEvents({
		programmeId,
		documentWorkflowInstanceId,
	});

	const latestReplacement = useMemo(() => {
		const events = eventsData ?? [];
		const replacements = events.filter(
			(event) => event.eventType === DocumentWorkflowEventLogType.DOCUMENT_FILE_REPLACED
		);
		if (replacements.length === 0) {
			return null;
		}
		const sorted = [...replacements].sort(
			(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
		);
		return sorted[0];
	}, [eventsData]);

	const closureEvent = useMemo(() => {
		const closures = (eventsData ?? []).filter(
			(event) =>
				event.eventType === DocumentWorkflowEventLogType.DOCUMENT_CLOSED_WITHOUT_SIGNATURE
		);
		if (closures.length === 0) {
			return null;
		}
		return [...closures].sort(
			(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
		)[0];
	}, [eventsData]);

	const replacementActor = latestReplacement ? resolveActor(latestReplacement.userAction) : '';
	const replacementComment = latestReplacement?.comment ?? '';
	const replacementTimestamp = latestReplacement
		? formatDateToUI(new Date(latestReplacement.createdAt), DATE_FORMATS.DATE_TIME_ALT)
		: '';

	return (
		<Flex direction="column" gap="1rem" p="1rem 1rem 0.5rem">
			<Flex direction="column" gap="0.25rem">
				<Text c="gray.9" size="0.875rem">
					Document name
				</Text>
				<Text c="dark.9" size="0.875rem">
					{fileName ?? '—'}
				</Text>
			</Flex>
			<Flex direction="column" gap="0.25rem">
				<Text c="gray.9" size="0.875rem">
					Created on
				</Text>
				<Text c="dark.9" size="0.875rem">
					{formatDisplayDate(createdAt)}
				</Text>
			</Flex>
			<Flex direction="column" gap="0.25rem">
				<Text c="gray.9" size="0.875rem">
					Last updated
				</Text>
				<Text c="dark.9" size="0.875rem">
					{formatDisplayDate(updatedAt)}
				</Text>
			</Flex>
			<Flex direction="column" gap="0.25rem">
				<Text c="gray.9" size="0.875rem">
					Current status
				</Text>
				<Flex align="center" gap="0.25rem">
					<div
						style={{
							width: 6,
							height: 6,
							backgroundColor: statusColor,
							borderRadius: '50%',
							padding: '0.06rem',
						}}
					/>
					<Text c="dark.9" size="0.875rem">
						{statusText}
					</Text>
				</Flex>
			</Flex>
			{dateAppliedAt && effectiveDate && (
				<Flex direction="column" gap="0.25rem">
					<Text c="gray.9" size="0.875rem">
						{tMipManage('documents_page.apply_date.details.applied_date_label')}
					</Text>
					{/* The date stamped on the document, not the timestamp the stamping ran at.
					    Rebuilt from its UTC parts because it is a calendar date, not an instant. */}
					<Text
						c="dark.9"
						size="0.875rem"
						data-testid={TEST_ELEMENT_IDS.DOCUMENT_DETAILS_APPLIED_DATE_VALUE}
					>
						{formatDisplayDate(parseBEDateIgnoreTimezone(effectiveDate))}
					</Text>
				</Flex>
			)}
			{(latestReplacement || closureEvent) && (
				<Flex direction="column" gap="0.5rem">
					{/* Internal-only label — deliberately not a Tolgee key. */}
					<Text c="gray.9" size="0.875rem">
						Additional
					</Text>
					{latestReplacement && (
						<Box
							p="md"
							sx={(theme) => ({
								backgroundColor: theme.colors.orange[0],
								borderRadius: theme.radius.sm,
							})}
						>
							<Stack gap="xs">
								<Group gap={8} wrap="nowrap">
									<Icon variant="refresh-v3" size="lg" color="#E8590C" />
									<Text c="orange.7" size="0.9375rem" fw={700}>
										{tMipManage('documents_page.replace_file.details.heading')}
									</Text>
								</Group>
								<Stack gap={2}>
									<Text size="0.875rem" c="gray.8">
										by{' '}
										<Text span fw={700} c="dark.9">
											{replacementActor || 'Support'}
										</Text>{' '}
										· Support
									</Text>
									<Text size="0.875rem" c="gray.7">
										{replacementTimestamp}
									</Text>
								</Stack>
								{replacementComment && (
									<>
										<Divider color="orange.3" />
										<Text size="0.9375rem" c="dark.9" fs="italic">
											&ldquo;{replacementComment}&rdquo;
										</Text>
									</>
								)}
							</Stack>
						</Box>
					)}
					{closureEvent && (
						<Box
							p="md"
							sx={(theme) => ({
								backgroundColor: theme.colors.gray[0],
								borderRadius: theme.radius.sm,
							})}
						>
							<Stack gap="xs">
								<Group gap={8} wrap="nowrap">
									<Icon variant="document-v3" size="lg" color="#495057" />
									<Text c="gray.8" size="0.9375rem" fw={700}>
										{tMipManage(
											'documents_page.close_document.details.heading'
										)}
									</Text>
								</Group>
								<Stack gap={2}>
									<Text size="0.875rem" c="gray.8">
										by{' '}
										<Text span fw={700} c="dark.9">
											{resolveActor(closureEvent.userAction)}
										</Text>
									</Text>
									<Text size="0.875rem" c="gray.7">
										{formatDateToUI(
											new Date(closureEvent.createdAt),
											DATE_FORMATS.DATE_TIME_ALT
										)}
									</Text>
								</Stack>
								{(closureEvent.comment ?? statusMessage) && (
									<>
										<Divider color="gray.3" />
										<Text size="0.9375rem" c="dark.9" fs="italic">
											&ldquo;{closureEvent.comment ?? statusMessage}&rdquo;
										</Text>
									</>
								)}
							</Stack>
						</Box>
					)}
				</Flex>
			)}
			{canCloseDocument({ workflowStatus, signerStatuses }) && (
				<CloseDocumentSection
					dealId={dealId}
					programmeId={programmeId}
					documentWorkflowInstanceId={documentWorkflowInstanceId}
				/>
			)}
			{canReplaceFile && (
				<ReplaceFileSection
					dealId={dealId}
					programmeId={programmeId}
					documentWorkflowInstanceId={documentWorkflowInstanceId}
					fileName={fileName}
					isReadOnly={isReadOnly}
				/>
			)}
		</Flex>
	);
};
